// src/app/core/chatPage/chatCopmonents/chatInForm/components/MessagesList.tsx
import { Fragment, RefObject, useEffect } from "react";
import { observer } from "mobx-react-lite";
import type { Message } from "../../shared/";
import { MessageItem } from "./MessageItem";
import { WelcomeBanner } from "./WelcomeBanner";
import { AgentProgress } from "./agent/AgentProgress";
import { AnimatePresence } from "motion/react";
import { TextGenerationViewModel } from "@/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel";

interface MessagesListProps {
  messages: Message[];
  messagesEndRef: RefObject<HTMLDivElement>;
  modelDisplayNames: Record<string, string>;
  selectedModelName: string;
  activeConversationTitle?: string | null;
  onRegenerate: (messageId: string) => void;
  onScroll?: () => void;
  containerRef?: RefObject<HTMLDivElement>;
  className?: string;
  onExampleClick?: (prompt: string) => void;
}

export const MessagesList = observer(function MessagesList({
  messages,
  messagesEndRef,
  modelDisplayNames,
  activeConversationTitle,
  onRegenerate,
  onScroll,
  containerRef,
  className = "",
  onExampleClick,
}: MessagesListProps) {
  const viewModel = TextGenerationViewModel.getInstance();

  const lastRegeneratableAiMessageId = (() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.sender === 'ai' && !message.isWaiting && message.id.startsWith('ai-')) {
        return message.id;
      }
    }
    return null;
  })();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages.length]);

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className={`flex-1 min-h-0 overflow-y-auto ${className}`}
      style={{
        maxWidth: 'var(--container-max-width, 672px)',
        margin: '0 auto',
        padding: '20px var(--container-padding, 9px) 0',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        width: '100%',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 40px), transparent 100%)',
        maskImage: 'linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 30px), transparent 100%)',
      }}
    >
      {messages.length === 0 && (
        <WelcomeBanner
          activeConversationTitle={activeConversationTitle}
          onExampleClick={onExampleClick}
        />
      )}

      {messages.map((message) => (
        <Fragment key={message.id}>
          <AnimatePresence>
            {message.sender === 'ai' &&
              message.agentSteps &&
              message.agentSteps.length > 0 &&
              !message.blocks &&
              !message.tools &&
              !message.mediaParts && (
                <AgentProgress
                  steps={message.agentSteps}
                  currentStep={message.step || 0}
                  isComplete={!message.isWaiting && !message.isStreaming}
                />
              )}
          </AnimatePresence>
          <MessageItem
            message={message}
            modelDisplayName={message.modelId ? modelDisplayNames[message.modelId] || null : null}
            modelDisplayNames={modelDisplayNames}
            selectedModelIdByType={viewModel.selectedModelIdByType}
            canRegenerate={message.id === lastRegeneratableAiMessageId}
            onRegenerate={onRegenerate}
          />
        </Fragment>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
});